import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../supabase/config';
import { ScrollText, Clock, Search, User, Filter, Lock, ShoppingCart, MapPin, Star, Trash2, Edit3, UserPlus, Award, MessageSquare, Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCustomers } from '../../context/CustomerContext';
import { useTranslation } from 'react-i18next';
import { anonymizeName, anonymizeEmail } from '../../utils/customer';
import FilterDropdown from './promotions/components/FilterDropdown';

const CUSTOMER_ACTIONS = [
    'CREATE_CUSTOMER', 'UPDATE_CUSTOMER', 'DELETE_CUSTOMER',
    'LOG_INTERACTION', 'UPDATE_POINTS', 'CHECK_IN',
    'REDEEM_REWARD', 'CREATE_ORDER', 'UPDATE_ORDER',
];

const LOGS_PER_PAGE = 30;

const CustomerLogs = ({ hideHeader = false }) => {
    const { userData, effectiveTenantId, hasPermission, permissionsLoading } = useAuth();
    const { customers } = useCustomers();
    const { t, i18n } = useTranslation();

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [actionFilter, setActionFilter] = useState('all');
    const [selectedLog, setSelectedLog] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [extraData, setExtraData] = useState(null);
    const [dateRange, setDateRange] = useState({ from: '', to: '' });
    const [currentPage, setCurrentPage] = useState(1);

    // Build customer lookup map for fast name resolution
    const customerMap = useMemo(() => {
        const map = {};
        (customers || []).forEach(c => {
            map[c.id] = `${c.firstName || ''} ${c.lastName || ''}`.trim();
            if (c.email) map[c.email] = `${c.firstName || ''} ${c.lastName || ''}`.trim();
        });
        return map;
    }, [customers]);

    useEffect(() => {
        if (!effectiveTenantId) return;

        const fetchLogs = async () => {
            setLoading(true);
            const { data, error } = await supabase
                .from('activities')
                .select('*, metadata')
                .eq('tenant_id', effectiveTenantId)
                .in('action', CUSTOMER_ACTIONS)
                .order('timestamp', { ascending: false })
                .limit(500);

            if (error) {
                console.error('CustomerLogs fetch error:', error);
                setLoading(false);
                return;
            }

            setLogs((data || []).map(row => ({
                id: row.id,
                action: row.action,
                details: row.details,
                userEmail: row.user_email,
                userId: row.user_id,
                metadata: row.metadata,
                timestamp: row.timestamp,
            })));
            setLoading(false);
        };

        fetchLogs();

        const channel = supabase
            .channel('customer-logs-' + effectiveTenantId)
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'activities',
                filter: `tenant_id=eq.${effectiveTenantId}`
            }, (payload) => {
                const row = payload.new;
                if (CUSTOMER_ACTIONS.includes(row.action)) {
                    setLogs(prev => [{
                        id: row.id,
                        action: row.action,
                        details: row.details,
                        userEmail: row.user_email,
                        userId: row.user_id,
                        metadata: row.metadata,
                        timestamp: row.timestamp,
                    }, ...prev]);
                }
            })
            .subscribe();

        return () => supabase.removeChannel(channel);
    }, [effectiveTenantId]);

    // Reset page on filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, actionFilter, dateRange]);

    const filteredLogs = useMemo(() => {
        return logs.filter(log => {
            const searchMatch = !searchTerm ||
                log.details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                log.userEmail?.toLowerCase().includes(searchTerm.toLowerCase());
            const actionMatch = actionFilter === 'all' || log.action === actionFilter;

            // Date range filter
            let dateMatch = true;
            if (dateRange.from) {
                const logDate = new Date(log.timestamp);
                const fromDate = new Date(dateRange.from);
                fromDate.setHours(0, 0, 0, 0);
                if (logDate < fromDate) dateMatch = false;
            }
            if (dateRange.to) {
                const logDate = new Date(log.timestamp);
                const toDate = new Date(dateRange.to);
                toDate.setHours(23, 59, 59, 999);
                if (logDate > toDate) dateMatch = false;
            }

            return searchMatch && actionMatch && dateMatch;
        });
    }, [logs, searchTerm, actionFilter, dateRange]);

    // Pagination
    const totalPages = Math.ceil(filteredLogs.length / LOGS_PER_PAGE);
    const paginatedLogs = filteredLogs.slice((currentPage - 1) * LOGS_PER_PAGE, currentPage * LOGS_PER_PAGE);

    // Resolve customer name from log details
    const resolveCustomerName = (log) => {
        if (!log.details) return null;
        const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
        const match = log.details.match(uuidRegex);
        if (match && customerMap[match[0]]) return anonymizeName(customerMap[match[0]]);
        // Try email
        const emailRegex = /[\w.-]+@[\w.-]+\.\w+/;
        const emailMatch = log.details.match(emailRegex);
        if (emailMatch && customerMap[emailMatch[0]]) return anonymizeName(customerMap[emailMatch[0]]);
        return null;
    };

    const getActionIcon = (action) => {
        switch (action) {
            case 'CREATE_CUSTOMER': return <UserPlus size={14} />;
            case 'UPDATE_CUSTOMER': return <Edit3 size={14} />;
            case 'DELETE_CUSTOMER': return <Trash2 size={14} />;
            case 'LOG_INTERACTION': return <MessageSquare size={14} />;
            case 'UPDATE_POINTS': return <Star size={14} />;
            case 'CHECK_IN': return <MapPin size={14} />;
            case 'REDEEM_REWARD': return <Award size={14} />;
            case 'CREATE_ORDER':
            case 'APPEND_ORDER':
            case 'UPDATE_ORDER': return <ShoppingCart size={14} />;
            default: return <ScrollText size={14} />;
        }
    };

    const getActionColor = (action) => {
        const colors = {
            'CREATE_CUSTOMER': '#10b981',
            'UPDATE_CUSTOMER': '#3b82f6',
            'DELETE_CUSTOMER': '#ef4444',
            'LOG_INTERACTION': '#8b5cf6',
            'UPDATE_POINTS': '#f59e0b',
            'CHECK_IN': '#06b6d4',
            'REDEEM_REWARD': '#ec4899',
            'CREATE_ORDER': '#10b981',
            'APPEND_ORDER': '#10b981',
            'UPDATE_ORDER': '#3b82f6',
        };
        return colors[action] || 'var(--text-muted)';
    };

    const getActionLabel = (action) => {
        const labels = {
            'CREATE_CUSTOMER': 'Müşteri Eklendi',
            'UPDATE_CUSTOMER': 'Müşteri Güncellendi',
            'DELETE_CUSTOMER': 'Müşteri Silindi',
            'LOG_INTERACTION': 'Etkileşim',
            'UPDATE_POINTS': 'Puan Güncellendi',
            'CHECK_IN': 'Check-in',
            'REDEEM_REWARD': 'Ödül Kullanıldı',
            'CREATE_ORDER': 'Sipariş Oluşturuldu',
            'UPDATE_ORDER': 'Sipariş Güncellendi',
            'APPEND_ORDER': 'Siparişe Ürün Eklendi',
            'UPDATE_ORDER_STATUS': 'Sipariş Durumu Değişti',
        };
        return labels[action] || action?.replace(/_/g, ' ');
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        try {
            const date = new Date(timestamp);
            if (isNaN(date.getTime())) return '';
            return new Intl.DateTimeFormat(i18n.language === 'tr' ? 'tr-TR' : 'en-US', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            }).format(date);
        } catch { return ''; }
    };

    const formatRelativeTime = (timestamp) => {
        if (!timestamp) return '';
        try {
            const now = new Date();
            const date = new Date(timestamp);
            const diffMs = now - date;
            const diffMin = Math.floor(diffMs / 60000);
            const diffHr = Math.floor(diffMs / 3600000);
            const diffDay = Math.floor(diffMs / 86400000);

            if (diffMin < 1) return 'Az önce';
            if (diffMin < 60) return `${diffMin} dk önce`;
            if (diffHr < 24) return `${diffHr} saat önce`;
            if (diffDay < 7) return `${diffDay} gün önce`;
            return formatDate(timestamp);
        } catch { return ''; }
    };

    const handleLogClick = async (log) => {
        setSelectedLog(log);
        setExtraData(null);

        if (log.metadata && typeof log.metadata === 'object' && Object.keys(log.metadata).length > 0) {
            if (log.metadata.items || log.metadata.addedItems) {
                setExtraData({
                    type: 'order',
                    data: {
                        ...log.metadata,
                        items: log.metadata.items || log.metadata.addedItems,
                        total_amount: log.metadata.totalAmount || log.metadata.newTotal || log.metadata.amount
                    }
                });
                return;
            }
            if (log.metadata.rewardTitle) {
                setExtraData({ type: 'reward', data: log.metadata });
                return;
            }
        }

        const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
        const match = log.details?.match(uuidRegex);
        const relatedId = match ? match[0] : null;

        setDetailLoading(true);
        try {
            if (['CREATE_ORDER', 'UPDATE_ORDER_STATUS', 'APPEND_ORDER'].includes(log.action) && relatedId) {
                const { data: salesData } = await supabase
                    .from('sales')
                    .select('*')
                    .eq('order_id', relatedId)
                    .maybeSingle();

                if (salesData) {
                    setExtraData({ type: 'order', data: salesData });
                } else {
                    const { data: orderData } = await supabase
                        .from('orders')
                        .select('*, applied_promotion_id, applied_promotion_title, applied_reward_code')
                        .eq('id', relatedId)
                        .maybeSingle();
                    if (orderData) setExtraData({ type: 'order', data: orderData });
                }
            } else if (log.action === 'REDEEM_REWARD' && relatedId) {
                const { data: rewardData } = await supabase
                    .from('discounts')
                    .select('title, required_points, value, type')
                    .eq('id', relatedId)
                    .maybeSingle();
                if (rewardData) setExtraData({ type: 'reward', data: rewardData });
            } else if (['CREATE_CUSTOMER', 'UPDATE_CUSTOMER', 'UPDATE_POINTS', 'CHECK_IN'].includes(log.action)) {
                let customerId = relatedId;
                if (!customerId) {
                    const emailRegex = /[\w.-]+@[\w.-]+\.\w+/;
                    const emailMatch = log.details?.match(emailRegex);
                    if (emailMatch) {
                        const { data: custByEmail } = await supabase
                            .from('customers')
                            .select('id')
                            .eq('email', emailMatch[0])
                            .maybeSingle();
                        if (custByEmail) customerId = custByEmail.id;
                    }
                }

                if (customerId && uuidRegex.test(customerId)) {
                    const { data: custData } = await supabase
                        .from('customers')
                        .select('first_name, last_name, points, total_points, email')
                        .eq('id', customerId)
                        .maybeSingle();
                    if (custData) setExtraData({ type: 'customer', data: custData });
                }
            }
        } catch (err) {
            console.error("Detail fetch error:", err);
        } finally {
            setDetailLoading(false);
        }
    };

    const LogDetailModal = ({ log, onClose }) => {
        if (!log) return null;
        const actionColor = getActionColor(log.action);
        const customerName = resolveCustomerName(log);

        return (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }} onClick={onClose}>
                <div style={{
                    maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto',
                    background: 'var(--bg-card)',
                    borderRadius: '32px',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.08)',
                    position: 'relative',
                    border: '1px solid rgba(0,0,0,0.03)'
                }} onClick={e => e.stopPropagation()}>

                    <header style={{ padding: '2rem 2.5rem', borderBottom: '1px solid var(--border)', background: 'linear-gradient(to bottom, rgba(var(--primary-rgb), 0.02), transparent)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{ width: '48px', height: '48px', borderRadius: '16px', background: `${actionColor}10`, color: actionColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {getActionIcon(log.action)}
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-main)' }}>{getActionLabel(log.action)}</h3>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                                        <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: 500 }}>{formatDate(log.timestamp)}</p>
                                        {customerName && (
                                            <span style={{
                                                fontSize: '0.7rem', fontWeight: 700,
                                                background: `${actionColor}12`, color: actionColor,
                                                padding: '2px 8px', borderRadius: '6px'
                                            }}>
                                                {customerName}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button onClick={onClose} style={{ background: 'var(--bg-main)', border: '1px solid var(--border)', borderRadius: '50%', width: '32px', height: '32px', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }} onMouseEnter={e => e.currentTarget.style.transform = 'rotate(90deg)'} onMouseLeave={e => e.currentTarget.style.transform = 'rotate(0)'}>✕</button>
                        </div>
                    </header>

                    <div style={{ padding: '2.5rem' }}>
                        <div style={{ marginBottom: '2.5rem' }}>
                            <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '1rem', fontWeight: 700 }}>İşlem Özeti</h4>
                            <div style={{ padding: '1.25rem 1.75rem', background: 'var(--bg-main)', borderRadius: '20px', border: '1px solid var(--border)', fontSize: '1rem', lineHeight: 1.6, color: 'var(--text-main)', fontWeight: 500 }}>
                                {log.details}
                            </div>
                        </div>

                        {detailLoading ? (
                            <div style={{ padding: '3rem', textAlign: 'center' }}>
                                <div className="animate-spin" style={{ width: '28px', height: '28px', border: '3px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto' }}></div>
                            </div>
                        ) : extraData?.type === 'order' ? (
                            <div className="fade-in">
                                <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '1.25rem', fontWeight: 700 }}>Sipariş İçeriği</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '2rem' }}>
                                    {extraData.data.items?.map((item, idx) => (
                                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: 'var(--bg-main)', borderRadius: '16px', border: '1px solid var(--border)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                                <div style={{ fontSize: '0.875rem', fontWeight: 800, color: 'var(--primary)', opacity: 0.6 }}>{item.quantity}x</div>
                                                <span style={{ fontSize: '0.9375rem', fontWeight: 600 }}>{item.name || item.prodName}</span>
                                            </div>
                                            <span style={{ fontSize: '0.9375rem', fontWeight: 700 }}>{((item.price || 0) * (item.quantity || 1)).toLocaleString()} ₺</span>
                                        </div>
                                    ))}
                                </div>

                                {extraData.data.applied_promotion_title && (
                                    <div style={{ marginBottom: '1.5rem', padding: '14px 20px', background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Star size={12} color="white" fill="white" />
                                        </div>
                                        <span style={{ fontSize: '0.875rem', color: '#065f46', fontWeight: 700 }}>{extraData.data.applied_promotion_title} Uygulandı</span>
                                    </div>
                                )}

                                <div style={{ padding: '1.75rem', background: 'var(--bg-main)', borderRadius: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', border: '1px solid var(--border)' }}>
                                    <div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '6px', fontWeight: 600 }}>TOPLAM TUTAR</div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--text-main)', letterSpacing: '-0.02em' }}>{extraData.data.amount || extraData.data.total_amount || 0} ₺</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '6px', fontWeight: 600 }}>KAZANILAN PUAN</div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#f59e0b', letterSpacing: '-0.02em' }}>+{extraData.data.points_earned || extraData.data.earned_points || 0}</div>
                                    </div>
                                </div>
                            </div>
                        ) : extraData?.type === 'reward' ? (
                            <div style={{ padding: '2rem', background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.05), transparent)', borderRadius: '24px', border: '1px solid rgba(236, 72, 153, 0.2)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '1.5rem' }}>
                                    <div style={{ width: '56px', height: '56px', borderRadius: '20px', background: '#ec4899', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Award size={28} />
                                    </div>
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 800 }}>{extraData.data.title}</h4>
                                        <span style={{ fontSize: '0.8125rem', color: '#be185d', fontWeight: 700 }}>Sadakat Ödülü</span>
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                                    <div style={{ padding: '16px', background: 'white', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                                        <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '4px' }}>MALİYET</div>
                                        <div style={{ fontSize: '1.125rem', fontWeight: 800, color: '#ec4899' }}>{extraData.data.required_points} Puan</div>
                                    </div>
                                    <div style={{ padding: '16px', background: 'white', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                                        <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '4px' }}>AVANTAJ</div>
                                        <div style={{ fontSize: '1.125rem', fontWeight: 800, color: 'var(--text-main)' }}>{extraData.data.type === 'percentage' ? `%${extraData.data.value} İndirim` : 'Hediye Ürün'}</div>
                                    </div>
                                </div>
                            </div>
                        ) : extraData?.type === 'customer' ? (
                            <div style={{ padding: '2rem', background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05), transparent)', borderRadius: '24px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '1.75rem' }}>
                                    <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #3b82f6', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.1)' }}>
                                        <User size={28} color="#3b82f6" />
                                    </div>
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800 }}>{anonymizeName(`${extraData.data.first_name || ''} ${extraData.data.last_name || ''}`)}</h4>
                                        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 500 }}>{anonymizeEmail(extraData.data.email)}</div>
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                                    <div style={{ padding: '16px', background: 'white', borderRadius: '16px', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                                        <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '6px' }}>MEVCUT BAKİYE</div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#3b82f6' }}>{extraData.data.points}</div>
                                    </div>
                                    <div style={{ padding: '16px', background: 'white', borderRadius: '16px', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                                        <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '6px' }}>ÖMÜR BOYU PUAN</div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#f59e0b' }}>{extraData.data.total_points}</div>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid var(--border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                    <div style={{
                                        width: '40px', height: '40px', borderRadius: '50%',
                                        background: 'var(--bg-main)', border: '1px solid var(--border)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '0.875rem', fontWeight: 800, color: 'var(--primary)'
                                    }}>
                                        {log.userEmail?.[0]?.toUpperCase() || '?'}
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{log.userEmail || 'Sistem Kararı'}</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>İşlemi Gerçekleştiren</div>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Log ID</div>
                                    <div style={{ fontSize: '0.6875rem', fontFamily: 'monospace', opacity: 0.6 }}>{log.id.split('-')[0]}...</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (permissionsLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <div className="animate-spin" style={{ width: '32px', height: '32px', border: '4px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%' }}></div>
            </div>
        );
    }

    if (!userData?.isSuperAdmin && !hasPermission('customers', 'view')) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <Lock size={48} style={{ color: 'var(--text-muted)', marginBottom: '1rem', opacity: 0.5 }} />
                <h3>{t('common.accessDenied')}</h3>
                <p style={{ color: 'var(--text-muted)' }}>{t('common.noPermission')}</p>
            </div>
        );
    }

    const uniqueActions = [...new Set(logs.map(l => l.action))];
    const filterOptions = [
        { id: 'all', label: 'Tüm İşlemler' },
        ...uniqueActions.map(action => ({
            id: action,
            label: getActionLabel(action) || action
        }))
    ];

    const hasActiveFilters = dateRange.from || dateRange.to;

    return (
        <div className="fade-in">
            {!hideHeader && (
                <header style={{ marginBottom: '2.5rem' }}>
                    <h1 style={{ fontWeight: 900, fontSize: '2.25rem', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>Müşteri Kayıtları</h1>
                    <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Müşterilerinizle ilgili tüm kayıtlar, işlem geçmişi ve aktiviteler.</p>
                </header>
            )}

            {/* Summary Bar */}
            <div style={{
                display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap'
            }}>
                {[
                    { label: 'Toplam Kayıt', value: filteredLogs.length, color: 'var(--primary)' },
                    ...uniqueActions.slice(0, 3).map(action => ({
                        label: getActionLabel(action),
                        value: filteredLogs.filter(l => l.action === action).length,
                        color: getActionColor(action)
                    }))
                ].map((stat, i) => (
                    <div key={i} style={{
                        padding: '0.625rem 1rem',
                        background: `${stat.color}08`,
                        border: `1px solid ${stat.color}20`,
                        borderRadius: '10px',
                        display: 'flex', alignItems: 'center', gap: '8px',
                        fontSize: '0.8125rem'
                    }}>
                        <span style={{ fontWeight: 800, color: stat.color }}>{stat.value}</span>
                        <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{stat.label}</span>
                    </div>
                ))}
            </div>

            {/* Filters Row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
                <div className="search-container" style={{ position: 'relative', width: '100%', maxWidth: '400px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder={t('common.search')}
                        style={{ paddingLeft: '40px', width: '100%', height: '46px', background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '12px', outline: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
                    />
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Date Range */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ position: 'relative' }}>
                            <Calendar size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
                            <input
                                type="date" value={dateRange.from}
                                onChange={e => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                                style={{
                                    height: '40px', paddingLeft: '32px', paddingRight: '10px',
                                    border: '1px solid var(--border)', borderRadius: '10px',
                                    background: 'var(--bg-card)', color: 'var(--text-main)',
                                    fontSize: '0.8125rem', outline: 'none', width: '155px'
                                }}
                            />
                        </div>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>-</span>
                        <div style={{ position: 'relative' }}>
                            <Calendar size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
                            <input
                                type="date" value={dateRange.to}
                                onChange={e => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                                style={{
                                    height: '40px', paddingLeft: '32px', paddingRight: '10px',
                                    border: '1px solid var(--border)', borderRadius: '10px',
                                    background: 'var(--bg-card)', color: 'var(--text-main)',
                                    fontSize: '0.8125rem', outline: 'none', width: '155px'
                                }}
                            />
                        </div>
                        {hasActiveFilters && (
                            <button onClick={() => setDateRange({ from: '', to: '' })} style={{
                                background: 'transparent', border: 'none', cursor: 'pointer',
                                color: 'var(--text-muted)', padding: '4px', display: 'flex'
                            }}>
                                <X size={16} />
                            </button>
                        )}
                    </div>
                    <FilterDropdown
                        options={filterOptions}
                        activeFilter={actionFilter}
                        setActiveFilter={setActionFilter}
                        t={t}
                    />
                </div>
            </div>

            {loading ? (
                <div style={{ padding: '4rem', textAlign: 'center' }}>
                    <div className="animate-spin" style={{ width: '32px', height: '32px', border: '4px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto' }}></div>
                </div>
            ) : filteredLogs.length === 0 ? (
                <div className="card glass" style={{ padding: '5rem 2rem', textAlign: 'center' }}>
                    <div style={{ width: '80px', height: '80px', background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)', borderRadius: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                        <ScrollText size={40} />
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                        {searchTerm || actionFilter !== 'all' || hasActiveFilters ? 'Eşleşen kayıt bulunamadı' : 'Henüz müşteri logu yok'}
                    </h3>
                    <p style={{ color: 'var(--text-muted)' }}>
                        {searchTerm || actionFilter !== 'all' || hasActiveFilters
                            ? 'Filtreleri değiştirerek tekrar deneyin.'
                            : 'Müşteri işlemleri gerçekleştikçe burada görünecektir.'}
                    </p>
                </div>
            ) : (
                <>
                    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(var(--primary-rgb), 0.02)' }}>
                                        <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.8125rem' }}>Tarih</th>
                                        <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.8125rem' }}>İşlem</th>
                                        <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.8125rem' }}>Müşteri</th>
                                        <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.8125rem' }}>Detay</th>
                                        <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.8125rem' }}>Yapan</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedLogs.map(log => {
                                        const actionColor = getActionColor(log.action);
                                        const customerName = resolveCustomerName(log);
                                        return (
                                            <tr
                                                key={log.id}
                                                onClick={() => handleLogClick(log)}
                                                style={{ borderBottom: '1px solid var(--border)', transition: 'all 0.15s', cursor: 'pointer' }}
                                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--primary-rgb), 0.02)'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                            >
                                                <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.8125rem' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)' }}>
                                                        <Clock size={14} />
                                                        <div>
                                                            <div>{formatRelativeTime(log.timestamp)}</div>
                                                            <div style={{ fontSize: '0.6875rem', opacity: 0.7 }}>{formatDate(log.timestamp)}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '1rem 1.5rem' }}>
                                                    <span style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                        fontSize: '0.75rem', fontWeight: 700,
                                                        padding: '5px 10px', borderRadius: '8px',
                                                        background: `${actionColor}12`,
                                                        color: actionColor,
                                                    }}>
                                                        {getActionIcon(log.action)}
                                                        {getActionLabel(log.action)}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '1rem 1.5rem', fontSize: '0.8125rem' }}>
                                                    {customerName ? (
                                                        <span style={{ fontWeight: 600, color: 'var(--text-main)' }}>{customerName}</span>
                                                    ) : (
                                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>-</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '1rem 1.5rem', fontSize: '0.875rem', maxWidth: '300px' }}>
                                                    <span style={{ color: 'var(--text-main)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.details}</span>
                                                </td>
                                                <td style={{ padding: '1rem 1.5rem' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{
                                                            width: '26px', height: '26px', borderRadius: '50%',
                                                            background: 'rgba(var(--primary-rgb), 0.1)',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: '0.6875rem', fontWeight: 700, color: 'var(--primary)',
                                                        }}>
                                                            {log.userEmail?.[0]?.toUpperCase() || '?'}
                                                        </div>
                                                        <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-muted)' }}>
                                                            {log.userEmail || 'Sistem'}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '1rem 0', marginTop: '1rem', flexWrap: 'wrap', gap: '0.75rem'
                        }}>
                            <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                                {filteredLogs.length} kayıttan {(currentPage - 1) * LOGS_PER_PAGE + 1}-{Math.min(currentPage * LOGS_PER_PAGE, filteredLogs.length)} arası
                            </span>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                <button
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(p => p - 1)}
                                    style={{
                                        padding: '6px 12px', borderRadius: '8px',
                                        border: '1px solid var(--border)', background: 'var(--bg-card)',
                                        color: 'var(--text-main)', cursor: 'pointer', display: 'flex',
                                        alignItems: 'center', gap: '4px', fontSize: '0.8125rem', fontWeight: 600,
                                        opacity: currentPage === 1 ? 0.4 : 1
                                    }}
                                >
                                    <ChevronLeft size={14} /> Önceki
                                </button>
                                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', padding: '0 8px' }}>
                                    {currentPage} / {totalPages}
                                </span>
                                <button
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(p => p + 1)}
                                    style={{
                                        padding: '6px 12px', borderRadius: '8px',
                                        border: '1px solid var(--border)', background: 'var(--bg-card)',
                                        color: 'var(--text-main)', cursor: 'pointer', display: 'flex',
                                        alignItems: 'center', gap: '4px', fontSize: '0.8125rem', fontWeight: 600,
                                        opacity: currentPage === totalPages ? 0.4 : 1
                                    }}
                                >
                                    Sonraki <ChevronRight size={14} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {selectedLog && <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />}

            <style>{`
                @media (max-width: 768px) {
                    .search-container { max-width: none !important; }
                }
            `}</style>
        </div>
    );
};

export default CustomerLogs;
